import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
    mdiMagnify,
    mdiPlus,
    mdiPencil,
    mdiTrashCan,
    mdiTable,
} from '@mdi/js';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Skeleton from '@/components/layout/Skeleton';
import AlertBox from '@/components/layout/AlertBox';
import Modal from '@/components/layout/Modal';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useDebounce } from '../chat/useDebounce';
import FormDataset from './FormDataset';
import RegistrosDataset from './RegistrosDataset';
import { Dataset, camposAtivos } from '@tera/shared/src/types/Dataset';
import styles from './page.module.css';

const Page = () => {
    const [search, setSearch] = React.useState('');
    const [selecionado, setSelecionado] = React.useState<Dataset | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [mostrarForm, setMostrarForm] = React.useState(false);
    const [registrosDe, setRegistrosDe] = React.useState<Dataset | null>(null);
    const [aExcluir, setAExcluir] = React.useState<number | null>(null);

    const buscaDebounce = useDebounce(search, 500);

    const query = useQuery(['dataset:search', buscaDebounce], async () => {
        const res = await axios.get('/api/dataset', {
            params: { txt: buscaDebounce || undefined, t: 50 },
        });
        return res.data;
    });

    const salvarMutation = useMutation(
        async (dataset: Dataset) => {
            setFieldErrors({});
            return dataset.id
                ? axios.put(`/api/dataset/${dataset.id}`, dataset)
                : axios.post('/api/dataset', dataset);
        },
        {
            onSuccess: () => {
                toast('Tabela salva com sucesso', {
                    type: 'success',
                    position: 'top-center',
                });
                query.refetch();
                setMostrarForm(false);
                setSelecionado(null);
            },
            onError: (error: any) => {
                setFieldErrors(error.response?.data?.fieldErrors || {});
                toast(error.response?.data?.msg ?? 'Erro ao salvar tabela', {
                    type: 'error',
                    position: 'top-center',
                    autoClose: 8000,
                });
            },
        },
    );

    const excluirMutation = useMutation(
        async (id: number) => axios.delete(`/api/dataset/${id}`),
        {
            onSuccess: () => {
                toast('Tabela excluída', { type: 'success', position: 'top-center' });
                query.refetch();
                setAExcluir(null);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao excluir tabela', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const abrirForm = (dataset: Dataset | null) => {
        setSelecionado(
            dataset ?? { chave: '', nome: '', descricao: '', campos: [], campos_chave: [] },
        );
        setMostrarForm(true);
    };

    const renderConteudo = () => {
        if (query.isLoading)
            return (
                <VBox gap={2}>
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                </VBox>
            );

        if (query.error)
            return (
                <AlertBox variant="danger">
                    <p>
                        Erro ao carregar tabelas de dados:{' '}
                        {(query.error as any)?.message || 'Erro desconhecido'}
                    </p>
                </AlertBox>
            );

        const cabecalhos: DataTableHeader[] = [
            { label: 'Nome', width: '30%', column: 'nome' },
            { label: 'Chave', width: '20%', column: 'chave' },
            {
                label: 'Campos',
                width: '10%',
                render: (linha: any) => (
                    <span>{camposAtivos({ campos: linha.campos ?? [] }).length}</span>
                ),
            },
            { label: 'Descrição', width: '25%', column: 'descricao' },
            {
                label: 'Ações',
                width: '160px',
                render: (linha: any) => (
                    <HBox gap={8}>
                        <Botao
                            variant="none-primary"
                            title="Registros"
                            icon={mdiTable}
                            onClick={() => setRegistrosDe(linha)}
                        />
                        <Botao
                            variant="none-primary"
                            title="Editar"
                            icon={mdiPencil}
                            onClick={() => abrirForm(linha)}
                        />
                        <Botao
                            variant="none-danger"
                            title="Excluir"
                            icon={mdiTrashCan}
                            onClick={() => setAExcluir(linha.id)}
                        />
                    </HBox>
                ),
            },
        ];

        return <DataTable cabecalhos={cabecalhos} linhas={query.data?.result ?? []} />;
    };

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap={24}>
                    <HBox>
                        <div className={styles.headerTitulo}>
                            <Titulo>Tabelas de dados</Titulo>
                            <Descricao>
                                Cadastros que o chatbot consulta para responder com valores exatos —
                                tabela de preços, prazos, catálogo. Diferente da base de
                                conhecimento, que responde a partir de documentos.
                            </Descricao>
                        </div>
                        <div className={styles.headerAcoes}>
                            <Botao
                                variant="primary"
                                icon={mdiPlus}
                                title="Nova tabela"
                                onClick={() => abrirForm(null)}
                            >
                                Nova tabela
                            </Botao>
                        </div>
                    </HBox>

                    <CampoTexto
                        nome="search"
                        rotulo=""
                        placeholder="Buscar tabela de dados..."
                        valor={search}
                        onChange={setSearch}
                        icone={mdiMagnify}
                    />

                    <div>{renderConteudo()}</div>
                </VBox>
            </div>

            <Modal
                instanceId="dataset-form-modal"
                show={mostrarForm}
                size="lg"
                title={selecionado?.id ? 'Editar tabela de dados' : 'Nova tabela de dados'}
                onClose={() => setMostrarForm(false)}
                onCancel={() => setMostrarForm(false)}
                onConfirm={() => selecionado && salvarMutation.mutate(selecionado)}
                confirmProps={{
                    label: 'Salvar',
                    variant: 'primary',
                    disabled: salvarMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <FormDataset
                    value={selecionado}
                    onChange={setSelecionado}
                    fieldErrors={fieldErrors}
                />
            </Modal>

            <Modal
                instanceId="dataset-registros-modal"
                show={!!registrosDe}
                size="lg"
                title={`Registros · ${registrosDe?.nome ?? ''}`}
                onClose={() => setRegistrosDe(null)}
                onCancel={() => setRegistrosDe(null)}
                cancelProps={{ label: 'Fechar' }}
            >
                {registrosDe && <RegistrosDataset dataset={registrosDe} />}
            </Modal>

            <Modal
                instanceId="dataset-delete-modal"
                show={!!aExcluir}
                size="sm"
                title="Confirmar exclusão"
                onClose={() => setAExcluir(null)}
                onCancel={() => setAExcluir(null)}
                onConfirm={() => aExcluir && excluirMutation.mutate(aExcluir)}
                confirmProps={{
                    label: 'Confirmar',
                    variant: 'danger',
                    disabled: excluirMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <p>Tem certeza que deseja excluir esta tabela e seus registros?</p>
                <p>Fluxos que consultam esta tabela deixarão de encontrar dados.</p>
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
